import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '0', 10);

  const article = useMemo(() => articles.find((a) => a.id === articleId), [articleId]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.id !== article.id)
      .slice(0, 4);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold text-foreground-900">文章未找到</h2>
          <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.createdAt,
    dateModified: article.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '丝袜小说精选合集',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://vipmvpda.cn/article/${article.id}`,
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://vipmvpda.cn',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.categoryName,
        item: `https://vipmvpda.cn/category/${article.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 丝袜小说精选合集`}
        description={article.excerpt}
        keywords={`丝袜小说,${article.categoryName},${article.tags.join(',')},丝袜小说免费阅读`}
        canonicalUrl={`https://vipmvpda.cn/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
        articleData={{
          publishedTime: article.createdAt,
          modifiedTime: article.updatedAt,
          author: article.author,
        }}
      />
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>

      <main className="min-h-screen pt-16">
        <div className="bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Link
                to={`/category/${article.category}`}
                className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors duration-200 whitespace-nowrap"
              >
                {article.categoryName}
              </Link>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-2 py-1 rounded-full bg-background-100 text-foreground-500 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 leading-snug">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500 flex-wrap">
              <span className="whitespace-nowrap">
                <i className="ri-user-line mr-1"></i>
                {article.author}
              </span>
              <span className="whitespace-nowrap">
                <i className="ri-calendar-line mr-1"></i>
                {article.createdAt}
              </span>
              <span className="whitespace-nowrap">
                <i className="ri-eye-line mr-1"></i>
                {article.reads.toLocaleString()} 阅读
              </span>
              <span className="whitespace-nowrap">
                <i className="ri-heart-line mr-1"></i>
                {article.likes.toLocaleString()} 赞
              </span>
              <span className="whitespace-nowrap">
                <i className="ri-file-text-line mr-1"></i>
                {article.wordCount.toLocaleString()} 字
              </span>
            </div>
          </header>

          <div className="mb-8 rounded-lg overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - 丝袜小说精选合集`}
              className="w-full h-auto object-cover max-h-96"
            />
          </div>

          <div
            className="prose prose-lg max-w-none text-foreground-800 leading-relaxed article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <button className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 hover:bg-accent-200 transition-colors duration-200 cursor-pointer">
                  <i className="ri-heart-line text-lg"></i>
                </button>
                <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors duration-200 cursor-pointer">
                  <i className="ri-bookmark-line text-lg"></i>
                </button>
                <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors duration-200 cursor-pointer">
                  <i className="ri-share-forward-line text-lg"></i>
                </button>
              </div>
              <Link
                to={`/category/${article.category}`}
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                查看更多{article.categoryName} <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">相关推荐</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} variant="default" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}